import React, { useState, useEffect } from 'react';
import { Form, Input, Radio, Upload, Button, message, Spin, Modal } from 'antd';
import { CloudUploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd';

interface MediaUploadFieldProps {
  form: FormInstance;
  fieldName: string | (string | number)[];
  label?: string;
  accept?: string;
  hint?: string;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
  absoluteFieldName?: string | (string | number)[];
}

export const MediaUploadField: React.FC<MediaUploadFieldProps> = ({
  form,
  fieldName,
  label,
  accept = "image/*",
  hint = "JPEG, PNG, PDG, and MP4 formats, up to 50MB",
  disabled = false,
  onUpload,
  absoluteFieldName,
}) => {
  const [imageInputMode, setImageInputMode] = useState<"upload" | "url">("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [stagingFile, setStagingFile] = useState<File | null>(null);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState<boolean>(false);

  const effectiveAbsoluteFieldName = absoluteFieldName || fieldName;
  const urlValue = Form.useWatch(effectiveAbsoluteFieldName, form);

  useEffect(() => {
    if (urlValue && !selectedFile) {
      setImageInputMode("url");
    }
  }, [urlValue]);

  const handleImageModeChange = (e: any) => {
    const nextMode = e.target.value;
    if (imageInputMode === "upload" && nextMode === "url" && selectedFile) {
      message.warning("Please remove the uploaded file first before providing a URL.");
      return;
    }
    if (imageInputMode === "url" && nextMode === "upload" && urlValue) {
      message.warning("Please clear the image URL first before uploading a file.");
      return;
    }
    setImageInputMode(nextMode);
  };

  const handleConfirmUpload = async () => {
    if (!stagingFile) return;
    try {
      setIsUploading(true);
      await onUpload(stagingFile);
      setSelectedFile(stagingFile);
      setStagingFile(null);
      setIsConfirmModalVisible(false);
    } catch (e) {
      message.error("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const cancelStaging = () => {
    setStagingFile(null);
    setIsConfirmModalVisible(false);
  };

  const isVideo = (url: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg') || lower.endsWith('.mov');
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Form.Item label={label ? `${label} Source` : "Media Source"} style={{ marginBottom: 16 }}>
        <Radio.Group
          value={imageInputMode}
          onChange={handleImageModeChange}
          optionType="button"
          buttonStyle="solid"
          style={{ width: "100%", display: "flex" }}
          disabled={disabled}
        >
          <Radio.Button value="upload" style={{ flex: 1, textAlign: "center" }}>
            Upload File
          </Radio.Button>
          <Radio.Button value="url" style={{ flex: 1, textAlign: "center" }}>
            Provide URL
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      {imageInputMode === "upload" ? (
        <>
          <Form.Item name={fieldName} hidden>
            <Input />
          </Form.Item>
          {!selectedFile && !urlValue ? (
            <Form.Item label={label ? `Upload ${label}` : "Upload Media"} required>
              <Upload.Dragger
                name="media"
                multiple={false}
                accept={accept}
                disabled={disabled}
                showUploadList={false}
                beforeUpload={(file) => {
                  setStagingFile(file);
                  setIsConfirmModalVisible(true);
                  return false;
                }}
              >
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined />
                </p>
                <p className="ant-upload-text">Choose a file or drag & drop it here</p>
                <p className="ant-upload-hint">{hint}</p>
              </Upload.Dragger>
            </Form.Item>
          ) : null}
        </>
      ) : (
        <Form.Item name={fieldName} label={label ? `${label} URL` : "Media URL"} required>
          <Input placeholder="https://..." disabled={disabled} />
        </Form.Item>
      )}

      {(selectedFile || urlValue) && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <p style={{ margin: 0, color: "#8c8c8c" }}>Media Preview</p>
            <Button 
              type="text" 
              danger 
              size="small" 
              icon={<DeleteOutlined />} 
              onClick={() => {
                setSelectedFile(null);
                form.setFieldValue(effectiveAbsoluteFieldName, "");
                setImageInputMode("upload");
              }}
              disabled={disabled}
            >
              Clear Image
            </Button>
          </div>
          {isVideo(urlValue) ? (
            <video
              src={selectedFile ? URL.createObjectURL(selectedFile) : urlValue}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                borderRadius: 8,
                border: "1px solid #f0f0f0",
              }}
            />
          ) : (
            <img
              src={selectedFile ? URL.createObjectURL(selectedFile) : urlValue}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                objectFit: "contain",
                borderRadius: 8,
                border: "1px solid #f0f0f0",
              }}
            />
          )}
        </div>
      )}

      <Modal
        title="Confirm Upload"
        open={isConfirmModalVisible}
        onOk={handleConfirmUpload}
        onCancel={cancelStaging}
        confirmLoading={isUploading}
        okText="Confirm & Upload"
        cancelText="Cancel"
        destroyOnClose
        maskClosable={!isUploading}
        closable={!isUploading}
      >
        <Spin spinning={isUploading} tip="Uploading...">
          <div style={{ textAlign: "center", padding: 16 }}>
            <p style={{ marginBottom: 16 }}>Are you sure you want to upload this file?</p>
            {stagingFile && (
              isVideo(stagingFile.name) ? (
                <video
                  src={URL.createObjectURL(stagingFile)}
                  controls
                  style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8, border: "1px solid #f0f0f0" }}
                />
              ) : (
                <img
                  src={URL.createObjectURL(stagingFile)}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 8, border: "1px solid #f0f0f0" }}
                />
              )
            )}
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>{stagingFile?.name}</p>
          </div>
        </Spin>
      </Modal>
    </div>
  );
};
